import React, { useState } from 'react';
import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { LuPencilLine } from 'react-icons/lu';
import { PendingActionPromptOutput, PendingOption } from '@/client';
import ChatPendingActionOption from './ChatPendingActionOption';
import { PENDING_MODE_CAPTION, PENDING_TYPE_COPY } from './chatPendingActionCopy';

interface ChatPendingActionCardProps {
	/**
	 * Whether this offer is still answerable is a backend fact, not something to infer from
	 * position in the conversation — an offer can survive a couple of turns (e.g. an unrelated
	 * guardrail redirect in between), so don't gate this on "is this the latest message." The
	 * backend nulls `pendingAction` itself once an offer is resolved/dropped/aged out; render
	 * whenever it's non-null and let a stale click just be handled as an ordinary message if it
	 * turns out the offer had already lapsed server-side.
	 */
	pendingAction: PendingActionPromptOutput;
	/** The assistant's own answer text for this turn — already states the specific claim or
	 * question being confirmed, so the card body reuses it rather than duplicating it. */
	responseText: string;
	/** Force-disables every option regardless of `hasSubmitted` — an escape hatch for callers, not
	 * wired to any computed staleness signal today. */
	disabled?: boolean;
	/** Sends `option.reply` as the next message, verbatim — see docs/local/chat-feature.md §C2 on
	 * why this exact string, not something derived from the option's position or label. */
	onSelectOption: (option: PendingOption) => void;
}

/**
 * Replaces the plain-text answer bubble when a turn ends by asking rather than answering. Two
 * shapes share this shell: a `confirm` pending renders a short pill per option (Save/Cancel); a
 * `select` pending renders one full-width row per candidate, because "yes" can't resolve a
 * which-one question — the visual distinction here IS the fix for that ambiguity.
 */
const ChatPendingActionCard: React.FC<ChatPendingActionCardProps> = (props: ChatPendingActionCardProps) => {
	const { pendingAction, responseText, disabled = false, onSelectOption } = props;
	// Guards against a double-click on the same render — once any option is chosen, disable the
	// rest immediately rather than waiting on a prop update from the caller.
	const [hasSubmitted, setHasSubmitted] = useState(false);

	const { eyebrow, title, icon } = PENDING_TYPE_COPY[pendingAction.pending_type];
	const isDisabled = disabled || hasSubmitted;

	const handleSelect = (option: PendingOption) => {
		if (isDisabled) return;
		setHasSubmitted(true);
		onSelectOption(option);
	};

	return (
		<VStack align="stretch" gap={2} maxW="lg">
			<HStack gap={1.5} color="fg.muted" textStyle="label.sm">
				<Icon as={LuPencilLine} boxSize={3.5} />
				<Text>{eyebrow}</Text>
			</HStack>

			<VStack
				align="stretch"
				gap={3}
				borderWidth="1px"
				borderColor="border.subtle"
				borderRadius="2xl"
				p={4}
				opacity={isDisabled ? 0.7 : 1}
			>
				<HStack gap={2}>
					<Icon as={icon} boxSize={4} color="fg.muted" />
					<Text fontWeight="semibold">{title}</Text>
				</HStack>

				<Text fontSize="sm" whiteSpace="pre-wrap">
					{responseText}
				</Text>

				<HStack justify="space-between" align="center" wrap="wrap" gap={2}>
					<Text fontSize="xs" color="fg.muted">
						{PENDING_MODE_CAPTION[pendingAction.mode]}
					</Text>
					{pendingAction.mode === 'confirm' && (
						<HStack gap={2}>
							{pendingAction.options.map((option) => (
								<ChatPendingActionOption
									key={option.reply}
									option={option}
									pendingType={pendingAction.pending_type}
									mode={pendingAction.mode}
									disabled={isDisabled}
									onSelect={() => handleSelect(option)}
								/>
							))}
						</HStack>
					)}
				</HStack>

				{pendingAction.mode === 'select' && (
					<VStack align="stretch" gap={2}>
						{pendingAction.options.map((option) => (
							<ChatPendingActionOption
								key={option.reply}
								option={option}
								pendingType={pendingAction.pending_type}
								mode={pendingAction.mode}
								disabled={isDisabled}
								onSelect={() => handleSelect(option)}
							/>
						))}
					</VStack>
				)}
			</VStack>
		</VStack>
	);
};

export default ChatPendingActionCard;
